import { useEffect } from "react";
import Lenis from "lenis";
import { gsap, ScrollTrigger, prefersReducedMotion } from "./motion";

/**
 * Lenis smooth scroll wired into GSAP's ticker so ScrollTrigger stays in sync.
 * Disabled entirely when the visitor prefers reduced motion.
 */
export default function useSmoothScroll() {
  useEffect(() => {
    if (prefersReducedMotion()) return undefined;

    // Ticker config, not a DOM touch — keep it in force from the first frame.
    gsap.ticker.lagSmoothing(0);

    let lenis = null;
    let raf = null;

    /**
     * Lenis measures the document (scroll offset, wrapper and content size) as
     * it constructs. This hook lives in App, so React runs it after every
     * section's effect has already rewritten the DOM, and measuring there
     * forces a synchronous layout of the whole page. A frame later the same
     * reads land on the layout the browser has just computed for the first
     * paint, and the mount task gets shorter. Until then the page scrolls
     * natively, and scrollToSection() already falls back to that.
     */
    const frame = requestAnimationFrame(() => {
      lenis = new Lenis({
        duration: 1.1,
        easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        smoothWheel: true,
        touchMultiplier: 1.6,
      });

      window.__lenis = lenis;

      raf = (time) => lenis.raf(time * 1000);
      gsap.ticker.add(raf);
      lenis.on("scroll", ScrollTrigger.update);
    });

    return () => {
      cancelAnimationFrame(frame);
      if (raf) gsap.ticker.remove(raf);
      if (lenis) lenis.destroy();
      delete window.__lenis;
    };
  }, []);
}

/** Anchor navigation that respects the Lenis instance when present. */
export function scrollToSection(hash) {
  const target = document.querySelector(hash);
  if (!target) return;

  if (window.__lenis) {
    window.__lenis.scrollTo(target, { offset: -70, duration: 1.35 });
  } else {
    const top = target.getBoundingClientRect().top + window.scrollY - 70;
    window.scrollTo({ top, behavior: "smooth" });
  }
}
